// src/utils/requestTracker.ts - ENHANCED: Support background operations
export type RequestType = 'search' | 'metadata' | 'schema' | 'health' | 'contacts' | 'background_search' | 'export' | 'get_lists' | 'create_list' | 'delete_list' | 'initial-suggestions-people' | 'initial-suggestions-contacts' | 'create_user' | 'list_users' | 'get_user' | 'update_user' | 'delete_user' | 'system_reset';

interface TrackedRequest {
  id: string;
  type: RequestType;
  timestamp: number;
  fields?: string[]; // For metadata requests
  abortController: AbortController;
  status: 'pending' | 'completed' | 'cancelled' | 'failed';
}

class RequestTracker {
  private activeRequests = new Map<string, TrackedRequest>();
  private requestsByType = new Map<RequestType, Set<string>>();
  private fieldRequests = new Map<string, string>(); // field -> requestId mapping
  private listeners = new Set<(state: RequestTrackerState) => void>();
  
  private updateTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private lastNotifiedState: RequestTrackerState | null = null;

  constructor() {
    // Initialize request type sets
    const types: RequestType[] = ['search', 'metadata', 'schema', 'health', 'contacts', 'background_search', 'export', 'get_lists', 'create_list', 'delete_list', 'initial-suggestions-people', 'initial-suggestions-contacts', 'create_user', 'list_users', 'get_user', 'update_user', 'delete_user', 'system_reset'];
    types.forEach(type => this.requestsByType.set(type, new Set()));
  }

  // Subscribe to state changes
  subscribe(listener: (state: RequestTrackerState) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Debounced state notifications
  private notifyListeners() {
    if (this.updateTimeoutId) {
      clearTimeout(this.updateTimeoutId);
    }
    
    this.updateTimeoutId = setTimeout(() => {
      const currentState = this.getState();
      
      // Only notify if state actually changed
      if (!this.lastNotifiedState || !this.statesEqual(currentState, this.lastNotifiedState)) {
        this.lastNotifiedState = { ...currentState };
        this.listeners.forEach(listener => listener(currentState));
      }
    }, 10); // Very short debounce to batch updates
  }

  // Compare states to prevent unnecessary updates
  private statesEqual(state1: RequestTrackerState, state2: RequestTrackerState): boolean {
    return (
      state1.isLoading === state2.isLoading &&
      state1.isSearching === state2.isSearching &&
      state1.isLoadingMetadata === state2.isLoadingMetadata &&
      state1.totalActive === state2.totalActive &&
      state1.loadingFields.size === state2.loadingFields.size &&
      Array.from(state1.loadingFields).every(field => state2.loadingFields.has(field))
    );
  }

  // Start tracking a new request
  startRequest(type: RequestType, fields?: string[]): TrackedRequest {
    const id = `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const abortController = new AbortController();
    
    const request: TrackedRequest = {
      id,
      type,
      timestamp: Date.now(),
      fields,
      abortController,
      status: 'pending'
    };

    // ENHANCED: Only cancel main search requests, not background ones
    if (type === 'search' || type === 'metadata') {
      this.cancelRequestsOfType(type);
    }
    // background_search requests don't cancel anything

    // Ensure a Set exists for this type before adding to it
    if (!this.requestsByType.has(type)) {
      this.requestsByType.set(type, new Set());
    }

    // Track the request
    this.activeRequests.set(id, request);
    this.requestsByType.get(type)!.add(id);

    // Track field-specific requests for metadata
    if (type === 'metadata' && fields) {
      fields.forEach(field => {
        const existingRequestId = this.fieldRequests.get(field);
        if (existingRequestId) {
          this.cancelRequest(existingRequestId);
        }
        this.fieldRequests.set(field, id);
      });
    }

    // Started request tracking
    this.notifyListeners();
    
    return request;
  }

  // Complete a request
  completeRequest(id: string, status: 'completed' | 'failed' = 'completed') {
    const request = this.activeRequests.get(id);
    if (!request) return;

    request.status = status;
    
    // Clean up field mappings
    if (request.type === 'metadata' && request.fields) {
      request.fields.forEach(field => {
        if (this.fieldRequests.get(field) === id) {
          this.fieldRequests.delete(field);
        }
      });
    }

    // Remove from active tracking after a short delay
    setTimeout(() => {
      this.activeRequests.delete(id);
      this.requestsByType.get(request.type)?.delete(id);
      this.notifyListeners();
    }, 100);

    // Completed request
    this.notifyListeners();
  }

  // Cancel a specific request
  cancelRequest(id: string): boolean {
    const request = this.activeRequests.get(id);
    if (!request || request.status !== 'pending') return false;

    request.status = 'cancelled';
    request.abortController.abort();
    
    // Clean up field mappings immediately
    if (request.type === 'metadata' && request.fields) {
      request.fields.forEach(field => {
        if (this.fieldRequests.get(field) === id) {
          this.fieldRequests.delete(field);
        }
      });
    }

    this.activeRequests.delete(id);
    this.requestsByType.get(request.type)?.delete(id);
    
    // Cancelled request
    this.notifyListeners();
    
    return true;
  }

  // Cancel all requests of a specific type
  cancelRequestsOfType(type: RequestType) {
    const requestIds = Array.from(this.requestsByType.get(type) || []);
    requestIds.forEach(id => this.cancelRequest(id));
  }

  // Check if a specific request is still active
  isRequestActive(id: string): boolean {
    const request = this.activeRequests.get(id);
    return request?.status === 'pending' || false;
  }

  // ENHANCED: Check if any request of a type is active (exclude background)
  hasActiveRequestOfType(type: RequestType): boolean {
    const requests = this.requestsByType.get(type) || new Set();
    return Array.from(requests).some(id => this.isRequestActive(id));
  }

  // Check if a field has an active metadata request
  isFieldLoading(field: string): boolean {
    const requestId = this.fieldRequests.get(field);
    return requestId ? this.isRequestActive(requestId) : false;
  }

  // Get loading fields
  getLoadingFields(): Set<string> {
    const loadingFields = new Set<string>();
    this.fieldRequests.forEach((requestId, field) => {
      if (this.isRequestActive(requestId)) {
        loadingFields.add(field);
      }
    });
    return loadingFields;
  }

  // ENHANCED: Get current state (exclude background operations from main indicators)
  getState(): RequestTrackerState {
    const activeByType: Record<RequestType, number> = {
      search: 0,
      metadata: 0,
      schema: 0,
      health: 0,
      contacts: 0,
      background_search: 0,
      export: 0,
      get_lists: 0,
      create_list: 0,
      delete_list: 0,
      'initial-suggestions-people': 0,
      'initial-suggestions-contacts': 0,
      create_user: 0,
      list_users: 0,
      get_user: 0,
      update_user: 0,
      delete_user: 0,
      system_reset: 0
    };

    this.activeRequests.forEach(request => {
      if (request.status === 'pending') {
        activeByType[request.type]++;
      }
    });

    return {
      // ENHANCED: Exclude background_search from main loading indicators
      isLoading: this.hasActiveRequestOfType('search') || this.hasActiveRequestOfType('metadata'),
      isSearching: this.hasActiveRequestOfType('search'),
      isLoadingMetadata: this.hasActiveRequestOfType('metadata'),
      loadingFields: this.getLoadingFields(),
      activeRequests: activeByType,
      totalActive: Object.values(activeByType).reduce((sum, count) => sum + count, 0)
    };
  }

  // Get request statistics
  getStats() {
    const stats = {
      total: this.activeRequests.size,
      byType: {} as Record<RequestType, number>,
      byStatus: {
        pending: 0,
        completed: 0,
        cancelled: 0,
        failed: 0
      }
    };

    this.activeRequests.forEach(request => {
      stats.byType[request.type] = (stats.byType[request.type] || 0) + 1;
      stats.byStatus[request.status]++;
    });

    return stats;
  }
}

export interface RequestTrackerState {
  isLoading: boolean;
  isSearching: boolean;
  isLoadingMetadata: boolean;
  loadingFields: Set<string>;
  activeRequests: Record<RequestType, number>;
  totalActive: number;
}

// Global instance
export const requestTracker = new RequestTracker();